import { Fragment, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "../components/chat/Logo";
import { Bubble, Typing } from "../components/chat/Bubble";
import { Composer } from "../components/chat/Composer";
import { LiveContent } from "../components/chat/LiveContent";
import { PushBanner } from "../components/chat/PushBanner";
import { firstVisitorIndex, statusLabel } from "../lib/hours";
import { OpenChromeBar } from "../components/chat/OpenChromeBar";
import { InstallButton } from "../components/support/InstallButton";
import { FaqList, HoursCard } from "../components/support/SupportInfo";
import { StartForm } from "../components/support/StartForm";
import { useLiveChat } from "../hooks/useLiveChat";
import { msgTime, phoneDigits } from "../lib/live";
import { reportInstalled } from "../lib/install";
import { useBrand } from "../context/brand";
import { VerifiedBadge } from "../components/chat/VerifiedBadge";

const HEAD = ["Fala com", "a gente."];

export default function SupportPage() {
  const { chat, cfg, msgs, status, reset } = useLiveChat();
  const [active, setActive] = useState(() => !!chat?.hasSession());
  const [uploading, setUploading] = useState(false);
  const [resumed, setResumed] = useState(false);
  const endRef = useRef(null);
  const brand = useBrand();
  const title = cfg?.title || brand.name;
  const sender = `${cfg?.agent_name || "Bia"} · ${title}`;

  useEffect(() => {
    if (active && chat?.hasSession()) {
      chat.resume();
      reportInstalled();
    }
  }, [active, chat]);

  useEffect(() => {
    if (active) endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [msgs, status?.typing, active]);

  const start = async (name, phone) => {
    if (!chat) throw new Error("Atendimento indisponível no momento. Tente novamente.");
    const d = await chat.start({ name, phone: phoneDigits(phone), origem: "cliente", funil: brand.id });
    setResumed(!!d?.resumed);
    reportInstalled();
    setActive(true);
  };

  const send = (t) => chat.send(t).catch((e) => toast.error(e?.message || "Não foi possível enviar."));
  const upload = (f) => {
    setUploading(true);
    chat.upload(f).catch((e) => toast.error(e?.message || "Não foi possível enviar o arquivo.")).finally(() => setUploading(false));
  };
  const newChat = () => { reset(); setResumed(false); setActive(false); };
  const offline = cfg && !cfg.online && !cfg.ai;

  return (
    <div className="sp-page" data-testid="support-page">
      <header className="sp-top">
        <LogoMark size={44} />
        <div className="flex-1 min-w-0">
          <p className="sp-brand truncate" data-testid="support-title">{title}<VerifiedBadge size={17} testId="support-verified-badge" /></p>
          <p className="text-[12.5px] text-[#9DB0A5] flex items-center gap-1.5" data-testid="support-status">
            <i className={`sp-dot ${cfg?.online || cfg?.ai ? "is-on" : ""}`} />
            {status?.typing ? <span className="text-[#D4FF3A]">digitando...</span> : statusLabel(cfg)}
          </p>
        </div>
        <InstallButton />
        {brand.subtitle && <p className="lz-subtitle sp-subtitle-row" data-testid="support-brand-subtitle">{brand.subtitle}</p>}
        {active && (
          <button data-testid="support-new-chat" onClick={newChat} className="sp-icon" aria-label="Nova conversa">
            <MessageSquarePlus size={18} />
          </button>
        )}
      </header>

      <main className="sp-main">
        <OpenChromeBar />
        {!active && (
          <h1 className="sp-h1" data-testid="support-heading">
            {HEAD.map((l, i) => (
              <span key={l} className="lz-mask">
                <motion.span className={i ? "text-[#D4FF3A]" : ""} style={{ display: "inline-block" }}
                  initial={{ y: "105%" }} animate={{ y: 0 }} transition={{ duration: 0.8, delay: 0.1 + i * 0.12, ease: [0.76, 0, 0.24, 1] }}>
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>
        )}
        {!active && <p className="text-[15px] text-[#C9D6CE] -mt-1">Já é cliente{brand.neutral ? "" : ` ${brand.name}`}? Tire dúvidas, envie prints e acompanhe a resposta por aqui.</p>}
        <HoursCard cfg={cfg} />
        {!active && <FaqList cfg={cfg} />}
        {!active && <StartForm onStart={start} />}

        {active && (
          <section className="sp-thread" data-testid="support-thread">
            {resumed && <div className="lz-day lz-resumed" data-testid="support-resumed-note">Continuando seu atendimento anterior</div>}
            {msgs.map((m, i) => (
              <Fragment key={m.id}>
                <Bubble from={m.from === "visitor" ? "me" : "bot"} time={msgTime(m.at)} testId={`support-message-${m.id}`}>
                  <LiveContent m={m} chat={chat} sender={m.from === "visitor" ? null : sender} />
                </Bubble>
                {i === firstVisitorIndex(msgs) && offline && cfg?.offline_text && (
                  <Bubble time={msgTime(m.at)} testId="support-offline-note">
                    <LiveContent m={{ id: "off", text: cfg.offline_text }} sender={sender} />
                  </Bubble>
                )}
              </Fragment>
            ))}
            <AnimatePresence>{status?.typing && <Typing key="t" />}</AnimatePresence>
            {msgs.some((m) => m.from === "visitor") && <PushBanner chat={chat} cfg={cfg} />}
            {active && <FaqList cfg={cfg} />}
            <div ref={endRef} className="h-24" />
          </section>
        )}
      </main>

      {active && (
        <Composer onSend={send} onFile={upload} uploading={uploading} onTyping={() => chat.typing()} placeholder="Escreva sua mensagem" />
      )}
    </div>
  );
}
